import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, Instagram, Mail, Phone, MapPin, Car, Search, Shield, Clock, Users } from 'lucide-react';

export function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative text-gray-300" style={{ backgroundColor: 'var(--hero-bg)' }}>
      {/* Subtle Yellow Top Accent Line */}
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ background: 'linear-gradient(90deg, transparent 0%, var(--footer-accent) 50%, transparent 100%)' }}
      />

      {/* Main Footer Content */}
      <div className="max-w-[1560px] mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        {/* Top Row - Stats/Trust Badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 pb-12 border-b border-gray-800">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-600/20 rounded-xl flex items-center justify-center">
              <Car className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">100+</p>
              <p className="text-sm text-gray-400">{t('home.stats.cars')}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-600/20 rounded-xl flex items-center justify-center">
              <MapPin className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">18</p>
              <p className="text-sm text-gray-400">{t('home.stats.cities')}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-600/20 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">100%</p>
              <p className="text-sm text-gray-400">Verified Dealers</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-600/20 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">24/7</p>
              <p className="text-sm text-gray-400">Online Support</p>
            </div>
          </div>
        </div>

        {/* Main Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img src="/images/logo.png" alt="KurdaCars" className="h-10 w-auto object-contain" />
              <div>
                <span className="text-2xl font-bold text-white">
                  {t('common.appName')}
                </span>
                <p className="text-xs text-primary-400">Trusted Car Marketplace</p>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-6 max-w-sm">
              {t('footer.aboutText')}
            </p>

            {/* Social Icons */}
            <div className="flex gap-3">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                style={{ ['--hover-bg' as string]: '#1877F2' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1877F2'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#E1306C] hover:to-[#F77737] rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://tiktok.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-black rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                aria-label="TikTok"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a
                href="https://wa.me/9647500000000"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-105"
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#25D366'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                aria-label="WhatsApp"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-5 flex items-center gap-2">
              <Search className="w-4 h-4 text-primary-400" />
              <span className="relative">
                {t('footer.quickLinks')}
                <span
                  className="absolute -bottom-1 left-0 w-8 h-0.5 rounded-full"
                  style={{ backgroundColor: 'var(--footer-accent)' }}
                />
              </span>
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="footer-link text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: 'var(--footer-accent)' }}
                  />
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link
                  to="/search"
                  className="footer-link text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: 'var(--footer-accent)' }}
                  />
                  {t('nav.search')}
                </Link>
              </li>
              <li>
                <Link
                  to="/favorites"
                  className="footer-link text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: 'var(--footer-accent)' }}
                  />
                  {t('nav.favorites')}
                </Link>
              </li>
              <li>
                <Link
                  to="/dealers"
                  className="footer-link text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: 'var(--footer-accent)' }}
                  />
                  {t('nav.dealers', 'Dealers')}
                </Link>
              </li>
              <li>
                <Link
                  to="/sell"
                  className="footer-link text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: 'var(--footer-accent)' }}
                  />
                  {t('nav.sellCar')}
                </Link>
              </li>
              <li>
                <Link
                  to="/support"
                  className="footer-link text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: 'var(--footer-accent)' }}
                  />
                  {t('footer.support')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Popular Brands */}
          <div>
            <h3 className="text-white font-semibold mb-5 flex items-center gap-2">
              <Car className="w-4 h-4 text-primary-400" />
              <span className="relative">
                Popular Brands
                <span
                  className="absolute -bottom-1 left-0 w-8 h-0.5 rounded-full"
                  style={{ backgroundColor: 'var(--footer-accent)' }}
                />
              </span>
            </h3>
            <ul className="space-y-3">
              {['Toyota', 'BMW', 'Mercedes', 'Hyundai', 'Kia', 'Nissan'].map((brand) => (
                <li key={brand}>
                  <Link
                    to={`/search?make=${brand}`}
                    className="footer-link text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ backgroundColor: 'var(--footer-accent)' }}
                    />
                    {brand}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-5 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary-400" />
              <span className="relative">
                {t('footer.contact')}
                <span
                  className="absolute -bottom-1 left-0 w-8 h-0.5 rounded-full"
                  style={{ backgroundColor: 'var(--footer-accent)' }}
                />
              </span>
            </h3>
            <ul className="space-y-4">
              <li>
                <a
                  href="https://maps.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-sm text-gray-400 hover:text-white transition-colors group"
                >
                  <MapPin className="w-5 h-5 text-primary-400 group-hover:text-[var(--footer-accent)] flex-shrink-0 mt-0.5 transition-colors" />
                  <span>Erbil</span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+9647500000000"
                  className="flex items-center gap-3 text-sm text-gray-400 hover:text-white transition-colors group"
                >
                  <Phone className="w-5 h-5 text-primary-400 group-hover:text-[var(--footer-accent)] flex-shrink-0 transition-colors" />
                  <span>+964 750 000 0000</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:kurdacars@gmail.com"
                  className="flex items-center gap-3 text-sm text-gray-400 hover:text-white transition-colors group"
                >
                  <Mail className="w-5 h-5 text-primary-400 group-hover:text-[var(--footer-accent)] flex-shrink-0 transition-colors" />
                  <span>kurdacars@gmail.com</span>
                </a>
              </li>
            </ul>

            {/* App Download Badges */}
            <div className="mt-6 pt-6 border-t border-gray-800">
              <p className="text-xs text-gray-500 mb-3">Coming Soon</p>
              <div className="flex gap-2">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg opacity-60">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-[8px] text-gray-400">Download on the</p>
                    <p className="text-xs font-medium">App Store</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg opacity-60">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-[8px] text-gray-400">GET IT ON</p>
                    <p className="text-xs font-medium">Google Play</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-[1560px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              {t('footer.copyright', { year: currentYear })}
            </p>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link
                to="/support"
                className="hover:text-white transition-colors relative group"
              >
                {t('footer.support')}
                <span
                  className="absolute -bottom-0.5 left-0 w-0 h-0.5 rounded-full group-hover:w-full transition-all duration-200"
                  style={{ backgroundColor: 'var(--footer-accent)' }}
                />
              </Link>
              <Link
                to="/privacy"
                className="hover:text-white transition-colors relative group"
              >
                {t('footer.privacy', 'Privacy Policy')}
                <span
                  className="absolute -bottom-0.5 left-0 w-0 h-0.5 rounded-full group-hover:w-full transition-all duration-200"
                  style={{ backgroundColor: 'var(--footer-accent)' }}
                />
              </Link>
              <a
                href="#"
                className="hover:text-white transition-colors relative group"
              >
                Terms of Service
                <span
                  className="absolute -bottom-0.5 left-0 w-0 h-0.5 rounded-full group-hover:w-full transition-all duration-200"
                  style={{ backgroundColor: 'var(--footer-accent)' }}
                />
              </a>
              <a
                href="#"
                className="hover:text-white transition-colors relative group"
              >
                Cookie Policy
                <span
                  className="absolute -bottom-0.5 left-0 w-0 h-0.5 rounded-full group-hover:w-full transition-all duration-200"
                  style={{ backgroundColor: 'var(--footer-accent)' }}
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
